import { SearchParams } from './common'

export interface Controller {
  id: string
  name: string
  address: string
  index: string
  decoderType: string
  type: string
  configuration: string
  configState: string
  attention: number
  value: number
  interfaceId: string
  locationId: number
  controllerConfigure: string
  controllerConfigureId: number
  configurationId: number
  decoderTypeId: number
  typeId: number
  discoverState: string
  twiId: number
  twiCustomName?: string
  // Switch parameters. PUT /api/Controller replaces the row, so anything that
  // edits a decoder has to read these and send them back unchanged.
  verify?: boolean
  force?: boolean
  inrushVoltage?: number
  inrushTime?: number
  holdingVoltage?: number
  overrideSwitchParams?: boolean
  overrideOptions?: boolean
}

export interface IOConfig {
  id: number
  description: string
  flags: number
  luTypeProtocolMask: number
  ioTypeMask: number
  config: string
  expression: string
  interfaceId: number
}

// An IO configuration applies to one SRC decoder type and one line unit
// protocol. Both are compared by equality, not as bit tests — that is what the
// API does, and every configuration row carries a single-bit value.
// See SRC "Interface and Controller API" §3.2.6.2 and §3.2.7.2.
export enum IoTypeMask {
  Output = 1,
  Input = 2,
  Sdi12 = 4,
}

export enum LuProtocol {
  Rkd = 1,
  Mk2 = 2,
  Mk3 = 4,
}

export const LU_PROTOCOL_LABEL: Record<number, string> = {
  [LuProtocol.Rkd]: 'src rkd',
  [LuProtocol.Mk2]: 'src mk2',
  [LuProtocol.Mk3]: 'src mk3',
}

// The wildcard "Unknown" configuration, offered for every decoder.
export const IO_CONFIG_UNKNOWN_ID = 0

export interface LineUnit {
  // The line unit id is the decoder address.
  id: number
  interfaceId: number
  type: number
}

export interface LineUnitType {
  id: number
  interfaceId: number
  name: string
  protocol: number
}

export interface IOConfiguration {
  id: number
  description: string
  flags: number
  luTypeProtocolMask: number
  ioTypeMask: number
  config: string
  expression: string
  interfaceId: number
}

export interface DecoderType {
  id: string
  name: string
}

export type DecoderUsageType =
  | 'station'
  | 'flowValve'
  | 'waterTankFillValve'
  | 'fertigation'
  | 'globalFertigation'
  | 'cisFertigation'
  | 'manualStationRun'
  | 'flowSensor'
  | 'waterTankLevelSensor'
  | 'rainInput'
  | 'fertigationWaterLevel'

export interface DecoderUsageItem {
  type: DecoderUsageType | string
  objectId: string
  name?: string
  outputIndex?: number
  role?: string | null
}

export interface DecoderUsageResult {
  address: number
  outputs: { index: number }[]
  usages: DecoderUsageItem[]
  inUse: boolean
  isOn: boolean
}

export enum DecoderDeleteErrorCode {
  InUse = 500011,
}

export interface UpdateMultipleController {
  interfaceId: number
  configuration: number
  page: number
  errors: string[]
}

export interface UpdateController {
  interfaceId: number
  address: string
  id: string
  name: string
  // Null for input and sdi-12 decoders, which have no purpose.
  decoderType: number | null
  configuration: number
  verify: boolean
  force: boolean
  inrushVoltage: number
  inrushTime: number
  holdingVoltage: number
  overrideSwitchParams: boolean
  overrideOptions: boolean
  twiId?: number
}

export interface SearchController extends SearchParams {
  interfaceId: number
  isRequiredStation?: boolean
  stationId?: number
  Purpose?: number[]
}

export interface SearchIOConfig extends SearchParams {
  interfaceId: number
}

export interface SearchIOConfigDecoder {
  interfaceId: number
  address: string
  type: number
}

export interface SearchControllerId extends SearchParams {
  interfaceId: number
  address: string
  id: string
}

export interface SearchControllerOn extends SearchParams {
  interfaceId: number
  address: string
  id: string
}
export interface CreateController {
  id: string
  address: number
  interfaceId: number
  type: number
}

export interface RunManualStation {
  id: number
  interfaceId: number
  runtime: number
  cycle: number
  soak: number
}

export interface OffStationMultiple {
  interfaceId: number
  stationIds: number[]
}

export interface RainOperationsForm {
  rainIO?: string
  rainStart: string
  rainBucket: string
  rainInputType?: number
}

export interface CreateOperationsRain {
  interfaceId: number
  rainDecoderId?: string
  // DECISION: the decoder tuple stays optional next to decoderId, not removed, because a backend without vnv-cisgenics-BE#405 still reads it. DEPRECATED SOON: remove once #405 is on production (SCRUM-1169).
  rainAddr?: number
  rainIdx?: number
  rainTwiId?: number
  rainStartDelay: number
  rainBucketSize?: number
  rainInputType?: number
}

export interface selectedController {
  address: string
  id: string
  interfaceId: string
  name: string
  status: boolean
}
export interface unSelectedController {
  address: string
  id: string
  interfaceId: string
  name: string
  status: boolean
}
export interface ControllerByLocationId {
  selectedControllers: selectedController[]
  unselectedControllers: unSelectedController[]
}
